import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { MedicationAdministration } from '@mona/models';
import { EncountersActions } from '@mona/pdms/data-access-encounters';
import { BalanceActions } from '../actions';

/** Medication administration entity state */
export interface MedicationsAdministrationState extends EntityState<MedicationAdministration> {
    /**
     * Error
     */
    error: any;
}

export const medicationAdministrationAdapter: EntityAdapter<MedicationAdministration> =
    createEntityAdapter<MedicationAdministration>();

export const medAdmInitialState: MedicationsAdministrationState = medicationAdministrationAdapter.getInitialState({
    error: null,
});

/**
 * Medication administration reducer.
 *
 * `loadMedicationAdministrationsSuccess` uses `setAll` so the snapshot fully replaces
 * local state. This is the canonical post-persist propagation path:
 *
 * - `BalanceEffects.onChangeSuccess$` listens to `persistChangesAction.succeededAction`,
 *   runs `mergeDataWithPersistedChanges(data, succeeded)` (which splices out persisted
 *   deletes), and dispatches `loadMedicationAdministrationsSuccess` with the resulting
 *   list. With `setAll`, deleted entries disappear from local state. With `upsertMany`,
 *   they would survive — which is the regression that produced the
 *   "delete administration not reflected" white-screen observed in the hospital.
 *
 * - `EncounterComponent.reconcileAfterPersist` re-fetches the list ~5s after persist as
 *   a safety net for dropped WS pushes; the response goes through the same reducer.
 *
 * The optimistic-state-preservation rationale behind the earlier `setAll → upsertMany`
 * migration (used for the post-persist reconcile contract on other reducers) does not
 * apply to MedicationAdministration: there is no UI path that adds an MA directly to
 * the entity store. UI changes go through the change-log and are applied via the
 * persist-success effect above.
 *
 * `BalanceActions.deleteMedicationAdministration` is also handled here so that
 * realtime WS deletes (`handleMedicationAdministrationDataUpdateMessage`) and any
 * direct deletion paths actually remove the entity from local state. Before this
 * handler existed the action was dispatched into the void and the entity hung around
 * until full encounter reload.
 */
export const medicationAdministrationReducer = createReducer(
    medAdmInitialState,
    on(BalanceActions.loadMedicationAdministrationsSuccess, (state, action) =>
        medicationAdministrationAdapter.setAll(action.medicationAdministrations, state),
    ),
    on(BalanceActions.upsertMedicationAdministration, (state, action) => {
        return medicationAdministrationAdapter.updateOne(
            {
                id: action.medicationAdministration.id,
                changes: action.medicationAdministration,
            },
            state,
        );
    }),
    on(BalanceActions.addMedicationAdministration, (state, action) => {
        return medicationAdministrationAdapter.upsertOne(action.medicationAdministration, state);
    }),
    on(BalanceActions.deleteMedicationAdministration, (state, action) =>
        medicationAdministrationAdapter.removeOne(action.id, state),
    ),
    on(BalanceActions.clearMedicationAdministrations, EncountersActions.clearEncounter, state =>
        medicationAdministrationAdapter.removeAll(state),
    ),
    on(BalanceActions.loadMedicationAdministrationsFailed, (state, action) => ({
        ...state,
        error: action.error,
    })),
    on(BalanceActions.loadMedicationAdministrationsCancelled, state => ({
        ...state,
        error: null,
    })),
);
